import React, { useEffect, useState } from 'react';
import { View, StyleSheet, ScrollView, RefreshControl, Alert as RNAlert, Platform, TouchableOpacity, Keyboard, type GestureResponderEvent } from 'react-native';
import { Card, Text, Button, List, Chip, Searchbar, FAB, Portal, Modal, IconButton } from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useSelector, useDispatch } from 'react-redux';
import { ResultsStackParamList } from '../../navigation/AppNavigator';
import { RootState, AppDispatch } from '../../store';
import { fetchReviews, deleteReview } from '../../store/slices/reviewSlice';
import { colors, spacing, typography, shadows } from '../../constants/theme';
import { ReviewStatus, HomeReview } from '../../types';
import { Text as RNText } from 'react-native';
import { api } from '../../services/api';
import { isDetoxTestControlsEnabled, shouldMockNativeSharing } from '../../config/environments';

// Import MaterialCommunityIcons conditionally for web compatibility
let MaterialCommunityIcons: any;
try {
  MaterialCommunityIcons = require('@expo/vector-icons').MaterialCommunityIcons;
} catch (e) {
  // Fallback for web - create a simple text-based icon component
  MaterialCommunityIcons = ({ name, size, color, ...props }: any) => {
    const iconMap: { [key: string]: string } = {
      'home': '🏠',
      'clipboard-list': '📋',
      'file-document-outline': '📄',
      'check-circle': '✓',
      'clock-outline': '⏱',
      'filter-variant': '▼',
      'magnify': '🔍',
      'plus': '+',
      'calendar': '📅',
      'file-pdf-box': '📄',
      'email-outline': '✉️',
      'delete': '🗑️',
    };
    return <RNText style={{ fontSize: size || 24, color }} {...props}>{iconMap[name] || '•'}</RNText>;
  };
}
import { SyncStatusBar } from '../../components/SyncStatusBar';
import { reviewService } from '../../services/reviewService';
import { generateVendorEmails } from '../../store/slices/vendorEmailSlice';
import { subscribeToDetoxResultFocus, subscribeToDetoxResultShare } from '../../utils/detoxTestControls';

// Web-compatible Alert
const Alert = {
  alert: (title: string, message?: string, buttons?: any[]) => {
    if (Platform.OS === 'web') {
      const alertMessage = message ? `${title}\n\n${message}` : title;
      window.alert(alertMessage);
      if (buttons && buttons[0] && buttons[0].onPress) {
        buttons[0].onPress();
      }
    } else {
      RNAlert.alert(title, message, buttons);
    }
  }
};

const stopPressPropagation = (event?: GestureResponderEvent) => {
  if (!event) {
    return;
  }

  if (typeof event.preventDefault === 'function') {
    event.preventDefault();
  }
  if (typeof event.stopPropagation === 'function') {
    event.stopPropagation();
  }

  const nativeEvent = event.nativeEvent as GestureResponderEvent['nativeEvent'] & {
    stopImmediatePropagation?: () => void;
  };
  nativeEvent.stopImmediatePropagation?.();
};

// Defensive handling for ReviewStatus enum
const ReviewStatusValues = ReviewStatus || {
  DRAFT: 'draft',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  SUBMITTED: 'submitted',
};

type NavigationProps = StackNavigationProp<ResultsStackParamList, 'ResultsList'>;

const ResultsScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProps>();
  const dispatch = useDispatch<AppDispatch>();
  
  const { reviews = [], isLoading } = useSelector((state: RootState) => state.review || { reviews: [], isLoading: false });
  const { user } = useSelector((state: RootState) => state.auth);
  const vendorEmailsState = useSelector((state: RootState) => state.vendorEmails || { 
    emails: {}, 
    isGenerating: {}, 
    isLoading: {}, 
    error: null 
  });
  
  // Get unique template types for filtering
  const uniqueTemplateTypes = Array.from(
    new Set((reviews || []).map(r => r?.template?.type).filter(Boolean))
  ).sort();
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [templateFilter, setTemplateFilter] = useState<string>('all');
  const [deletingReviewId, setDeletingReviewId] = useState<number | null>(null);
  const [shareStatusByReviewId, setShareStatusByReviewId] = useState<Record<number, string>>({});
  const [detoxFocusedResultId, setDetoxFocusedResultId] = useState<number | null>(null);

  useEffect(() => {
    loadReviews();
  }, []);

  // Add focus listener to refresh data when returning to this screen
  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      loadReviews();
    });

    return unsubscribe;
  }, [navigation]);

  useEffect(() => {
    return subscribeToDetoxResultFocus(setDetoxFocusedResultId);
  }, []);

  const loadReviews = async () => {
    try {
      await dispatch(fetchReviews()).unwrap();
    } catch (error) {
      console.error('Failed to load reviews:', error);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadReviews();
    setRefreshing(false);
  };

  // Filter to only show completed/submitted reviews
  const completedReviews = reviews.filter(review => {
    if (!review || !review.status) return false;
    return review.status === ReviewStatusValues.COMPLETED || review.status === ReviewStatusValues.SUBMITTED;
  });

  const filteredReviews = completedReviews.filter(review => {
    if (!review) return false;
    const reviewAddress = review.property?.address || review.address || '';
    
    const matchesSearch = !searchQuery || 
      reviewAddress.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (review.property?.company?.name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (review.memberName?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (review.homeManagerName?.toLowerCase() || '').includes(searchQuery.toLowerCase());
    
    const matchesTemplate = templateFilter === 'all' || 
      review.template?.type === templateFilter;
    
    return matchesSearch && matchesTemplate;
  });
  const detoxSearchQuery = searchQuery.trim().toLowerCase();
  const detoxFocusedReview = isDetoxTestControlsEnabled
    ? (
      detoxFocusedResultId
        ? completedReviews.find(review => Number(review.id) === detoxFocusedResultId) || null
        : detoxSearchQuery
        ? filteredReviews.find(review =>
          ((review.property?.address || review.address || '').toLowerCase()) === detoxSearchQuery
        ) || filteredReviews.find(review =>
          ((review.property?.address || review.address || '').toLowerCase()).includes(detoxSearchQuery)
        )
        : filteredReviews.length === 1
          ? filteredReviews[0]
          : null
    )
    : null;

  const getStatusColor = (status: ReviewStatus | string) => {
    switch (status) {
      case ReviewStatusValues.COMPLETED:
        return colors.success;
      case ReviewStatusValues.SUBMITTED:
        return colors.primary;
      default:
        return colors.gray500;
    }
  };

  const getCompletionStats = (review: HomeReview) => {
    try {
      if (!review?.template?.categories || !review?.responses) {
        return { total: 0, completed: 0, percentage: 0 };
      }
      
      const allItems = review.template.categories
        .filter(c => c && c.items)
        .flatMap(c => c.items || []);
      
      const completedItems = allItems.filter(item => 
        item && review.responses?.some(r => r?.itemId === item.id && r?.status === 'completed')
      );
      
      return {
        total: allItems.length,
        completed: completedItems.length,
        percentage: allItems.length > 0 ? Math.round((completedItems.length / allItems.length) * 100) : 0
      };
    } catch (error) {
      console.error('Error calculating completion stats:', error);
      return { total: 0, completed: 0, percentage: 0 };
    }
  };

  const handleDeleteReview = (reviewId: number, address: string) => {
    // For web compatibility, use window.confirm instead of Alert.alert
    if (typeof window !== 'undefined') {
      const confirmed = window.confirm(`Are you sure you want to delete the review for ${address}? This action cannot be undone.`);
      if (confirmed) {
        setDeletingReviewId(reviewId);
        dispatch(deleteReview(reviewId))
          .unwrap()
          .then(() => {
            setDeletingReviewId(null);
          })
          .catch((error) => {
            setDeletingReviewId(null);
            window.alert('Failed to delete review. Please try again.');
          });
      }
    } else {
      // Mobile - use Alert.alert
      Alert.alert(
        'Delete Review',
        `Are you sure you want to delete the review for ${address}? This action cannot be undone.`,
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                setDeletingReviewId(reviewId);
                await dispatch(deleteReview(reviewId)).unwrap();
                setDeletingReviewId(null);
              } catch (error) {
                setDeletingReviewId(null);
                Alert.alert('Error', 'Failed to delete review. Please try again.');
              }
            },
          },
        ]
      );
    }
  };

  const handleGenerateVendorEmails = async (review: HomeReview) => {
    try {
      const result = await dispatch(generateVendorEmails({ reviewId: review.id })).unwrap();
      
      if (result.result.errors && result.result.errors.length > 0) {
        Alert.alert(
          'Partial Success',
          `Generated ${result.result.generatedEmails?.length || 0} emails, but ${result.result.errors.length} failed.`
        );
      } else {
        const emailCount = result.result.generatedEmails?.length || 0;
        Alert.alert(
          'Success',
          `Generated ${emailCount} vendor emails successfully!`
        );
        // Also navigate to the vendor emails screen after success
        navigation.navigate('VendorEmails', { reviewId: review.id });
      }
    } catch (error) {
      console.error('[ResultsScreen] Failed to generate vendor emails:', error);
      Alert.alert('Error', `Failed to generate vendor emails: ${error instanceof Error ? error.message : 'Please try again.'}`);
    }
  };

  const handleMockPDFShare = async (review: HomeReview) => {
    try {
      if (isDetoxTestControlsEnabled || shouldMockNativeSharing) {
        setShareStatusByReviewId(prev => ({
          ...prev,
          [review.id]: 'PDF share prepared',
        }));
        return;
      }

      await api.exportReviewPDF(review.id);
      setShareStatusByReviewId(prev => ({
        ...prev,
        [review.id]: 'PDF share prepared',
      }));
      Alert.alert('Share Prepared', 'PDF report is ready to share.');
    } catch (error) {
      Alert.alert('Export Failed', 'Failed to export review data. Please try again.');
    }
  };

  useEffect(() => {
    return subscribeToDetoxResultShare((reviewId) => {
      const review = completedReviews.find(candidate => Number(candidate.id) === reviewId);
      if (!review) {
        console.warn('[ResultsScreen] Detox result share target not found:', { reviewId });
        return;
      }

      handleMockPDFShare(review);
    });
  }, [completedReviews, handleMockPDFShare]);

  const renderReviewCard = (review: HomeReview) => {
    if (!review) return null;
    
    const stats = getCompletionStats(review);
    const submittedDate = review?.submittedAt ? new Date(review.submittedAt).toLocaleDateString() : null;
    const completedDate = review?.completedAt ? new Date(review.completedAt).toLocaleDateString() : null;
    const showDetoxShareButton = isDetoxTestControlsEnabled;

    return (
      <View key={review.id} style={styles.resultCardContainer}>
        {showDetoxShareButton && (
          <View style={styles.detoxShareControls}>
            <Button
              testID={`result-card-export-pdf-button-${review.id}`}
              mode="outlined"
              compact
              icon="share-variant"
              style={styles.detoxShareButton}
              onPress={() => handleMockPDFShare(review)}
            >
              Share PDF
            </Button>
            {shareStatusByReviewId[review.id] && (
              <Text
                testID={`result-card-share-status-${review.id}`}
                style={[styles.shareStatus, styles.detoxShareStatus]}
              >
                {shareStatusByReviewId[review.id]}
              </Text>
            )}
          </View>
        )}
        <TouchableOpacity
          testID={`result-card-${review.id}`}
          onPress={() => {
            if (deletingReviewId === review.id) {
              return;
            }
            navigation.navigate('ResultDetail', { reviewId: review.id });
          }}
          activeOpacity={0.95}
          disabled={deletingReviewId === review.id}
        >
        <Card style={styles.reviewCard}>
        <Card.Content>
          <View style={styles.cardHeader}>
            <View style={styles.addressContainer}>
              <Text
                testID={`result-card-address-${review.id}`}
                style={styles.propertyAddress}
                numberOfLines={2}
              >
                {review.property?.address || 'Unknown Address'}
              </Text>
              <Text style={styles.companyName}>
                {review.template?.name || 'Standard Review'}
              </Text>
              <Text testID={`result-card-member-name-${review.id}`} style={styles.memberName}>
                {review.memberName || 'Member name not set'}
              </Text>
            </View>
            <View style={styles.chipContainer}>
              <Chip
                mode="flat"
                style={[styles.statusChip, { backgroundColor: getStatusColor(review.status) }]}
                textStyle={{ color: '#fff', fontSize: 12 }}
              >
                {review.status === ReviewStatusValues.SUBMITTED ? 'Submitted' : 'Completed'}
              </Chip>
              {user?.role === 'admin' && (
                <IconButton
                  icon="delete"
                  size={20}
                  iconColor={colors.error}
                  onPress={(e) => {
                    stopPressPropagation(e);
                    handleDeleteReview(review.id, review.property?.address || 'Unknown Address');
                  }}
                  disabled={deletingReviewId === review.id}
                />
              )}
            </View>
          </View>

          <View style={styles.statsContainer}>
            <View style={styles.statItem}>
              <MaterialCommunityIcons name="check-circle" size={20} color={colors.success} />
              <Text style={styles.statText}>All items completed</Text>
            </View>
            <View style={styles.statItem}>
              <MaterialCommunityIcons name="calendar" size={16} color={colors.textSecondary} />
              <Text style={styles.statText}>
                {submittedDate || completedDate || 'No date'}
              </Text>
            </View>
          </View>

          {review.vendorNotes && (
            <View style={styles.notesPreview}>
              <MaterialCommunityIcons name="note-text" size={14} color={colors.textSecondary} />
              <Text style={styles.notesText} numberOfLines={2}>
                {review.vendorNotes}
              </Text>
            </View>
          )}
        </Card.Content>
        
        <Card.Actions style={styles.cardActions}>
          <Button
            testID={`view-result-button-${review.id}`}
            mode="outlined"
            onPress={() => {
              // Prevent navigation if deletion is in progress
              if (deletingReviewId === review.id) {
                return;
              }
              navigation.navigate('ResultDetail', { reviewId: review.id });
            }}
            icon="eye"
            disabled={deletingReviewId === review.id}
          >
            View Results
          </Button>
          {review.status === ReviewStatusValues.SUBMITTED && (
            <Button
              testID={`generate-vendor-emails-button-${review.id}`}
              mode="outlined"
              onPress={() => handleGenerateVendorEmails(review)}
              icon="email"
              loading={vendorEmailsState.isGenerating?.[review.id] || false}
            >
              Generate Vendor Emails
            </Button>
          )}
        </Card.Actions>
        {!showDetoxShareButton && shareStatusByReviewId[review.id] && (
          <Text testID={`result-card-share-status-${review.id}`} style={styles.shareStatus}>
            {shareStatusByReviewId[review.id]}
          </Text>
        )}
      </Card>
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <View testID="results-screen" style={styles.container}>
      <SyncStatusBar />
      
      <View style={styles.searchContainer}>
        <Searchbar
          testID="results-search-input"
          placeholder="Search by address..."
          onChangeText={setSearchQuery}
          onSubmitEditing={Keyboard.dismiss}
          blurOnSubmit
          value={searchQuery}
          style={styles.searchBar}
        />
        <Button
          testID="results-filter-button"
          mode="outlined"
          icon="filter"
          onPress={() => setShowFilters(true)}
          style={styles.filterButton}
          compact
        >
          Filter
        </Button>
      </View>
      {isDetoxTestControlsEnabled && (
        <>
          <Text
            testID="results-filtered-review-ids"
            style={styles.detoxStateMarker}
          >
            {`query=${searchQuery}|focus=${detoxFocusedResultId ?? ''}|active=${detoxFocusedReview?.id ?? ''}|share=${detoxFocusedReview ? shareStatusByReviewId[detoxFocusedReview.id] ?? '' : ''}|reviews=${filteredReviews
              .map((review) => `${review.id}:${review.property?.address || review.address || ''}`)
              .join('|')}`}
          </Text>
          {detoxFocusedReview && (
            <View
              testID={`detox-result-action-row-${detoxFocusedReview.id}`}
              style={styles.detoxFocusedActionRow}
            >
              <Button
                testID={`detox-result-export-pdf-button-${detoxFocusedReview.id}`}
                mode="outlined"
                compact
                style={styles.detoxFocusedActionButton}
                onPress={() => handleMockPDFShare(detoxFocusedReview)}
              >
                PDF
              </Button>
              <Button
                testID={`detox-result-open-button-${detoxFocusedReview.id}`}
                mode="outlined"
                compact
                style={[
                  styles.detoxFocusedActionButton,
                  deletingReviewId === detoxFocusedReview.id && styles.detoxFocusedActionButtonDisabled,
                ]}
                onPress={() => navigation.navigate('ResultDetail', { reviewId: detoxFocusedReview.id })}
                disabled={deletingReviewId === detoxFocusedReview.id}
              >
                Open
              </Button>
              {shareStatusByReviewId[detoxFocusedReview.id] && (
                <Text
                  testID={`detox-result-share-status-${detoxFocusedReview.id}`}
                  style={styles.detoxFocusedActionStatus}
                >
                  {shareStatusByReviewId[detoxFocusedReview.id]}
                </Text>
              )}
            </View>
          )}
        </>
      )}

      <ScrollView
        testID="results-scroll"
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {filteredReviews.length === 0 ? (
          <View style={styles.emptyState}>
            <MaterialCommunityIcons name="chart-line" size={64} color={colors.textSecondary} />
            <Text style={styles.emptyTitle}>No Results Found</Text>
            <Text style={styles.emptySubtitle}>
              {searchQuery 
                ? 'No completed reviews match your search criteria.'
                : 'No completed reviews available yet.'
              }
            </Text>
          </View>
        ) : (
          filteredReviews.map((review) => (
            <View key={review.id}>
              {renderReviewCard(review)}
            </View>
          ))
        )}
      </ScrollView>

      {/* Filter Modal */}
      <Portal>
        <Modal
          visible={showFilters}
          onDismiss={() => setShowFilters(false)}
          contentContainerStyle={styles.modalContent}
        >
          <Text style={styles.modalTitle}>Filter Results</Text>
          
          <Text style={styles.filterLabel}>Template Type</Text>
          <List.Section>
            <List.Item
              title="All"
              left={() => (
                <List.Icon
                  icon={templateFilter === 'all' ? 'check-circle' : 'circle-outline'}
                  color={templateFilter === 'all' ? colors.primary : colors.textSecondary}
                />
              )}
              onPress={() => setTemplateFilter('all')}
            />
            {uniqueTemplateTypes.map((templateType) => {
              // Get the template name for display
              const template = reviews.find(r => r.template?.type === templateType)?.template;
              const displayName = template?.name || templateType;
              
              return (
                <List.Item
                  key={templateType}
                  title={displayName}
                  left={() => (
                    <List.Icon
                      icon={templateFilter === templateType ? 'check-circle' : 'circle-outline'}
                      color={templateFilter === templateType ? colors.primary : colors.textSecondary}
                    />
                  )}
                  onPress={() => setTemplateFilter(templateType)}
                />
              );
            })}
          </List.Section>
          
          <View style={styles.modalActions}>
            <Button
              mode="outlined"
              onPress={() => setShowFilters(false)}
              style={styles.modalButton}
            >
              Close
            </Button>
          </View>
        </Modal>
      </Portal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  searchContainer: {
    flexDirection: 'row',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    gap: spacing.sm,
  },
  searchBar: {
    flex: 1,
  },
  detoxStateMarker: {
    height: 1,
    opacity: 0.01,
  },
  detoxFocusedActionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.xs,
    gap: spacing.sm,
  },
  detoxFocusedActionButton: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 40,
    minWidth: 72,
    paddingHorizontal: spacing.sm,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: colors.primary,
    backgroundColor: colors.surface,
  },
  detoxFocusedActionButtonDisabled: {
    opacity: 0.4,
  },
  detoxFocusedActionButtonText: {
    ...typography.caption,
    color: colors.primary,
    fontWeight: '600',
  },
  detoxFocusedActionStatus: {
    ...typography.caption,
    color: colors.success,
    flexShrink: 1,
  },
  filterButton: {
    alignSelf: 'center',
  },
  scrollContent: {
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.xl,
  },
  resultCardContainer: {
    position: 'relative',
  },
  reviewCard: {
    marginBottom: spacing.md,
    ...shadows.sm,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: spacing.md,
  },
  addressContainer: {
    flex: 1,
    marginRight: spacing.sm,
  },
  propertyAddress: {
    ...typography.h4,
    color: colors.text,
    marginBottom: spacing.xs,
  },
  companyName: {
    ...typography.body2,
    color: colors.textSecondary,
  },
  memberName: {
    ...typography.body2,
    color: colors.text,
    fontWeight: '600',
  },
  chipContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statusChip: {
    alignSelf: 'flex-start',
  },
  detoxShareButton: {
    backgroundColor: colors.surface,
  },
  detoxShareControls: {
    position: 'absolute',
    top: spacing.sm,
    right: spacing.sm,
    zIndex: 2,
    alignItems: 'flex-end',
  },
  cardActions: {
    justifyContent: 'flex-start',
  },
  shareStatus: {
    ...typography.caption,
    color: colors.success,
    marginHorizontal: spacing.md,
    marginBottom: spacing.md,
  },
  detoxShareStatus: {
    marginHorizontal: 0,
    marginTop: spacing.xs,
    marginBottom: 0,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 4,
    backgroundColor: colors.surface,
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: spacing.sm,
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  statText: {
    ...typography.caption,
    marginLeft: spacing.xs,
    color: colors.text,
  },
  notesPreview: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: spacing.sm,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.gray200,
  },
  notesText: {
    ...typography.caption,
    marginLeft: spacing.xs,
    color: colors.textSecondary,
    flex: 1,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: spacing.xxl,
    paddingHorizontal: spacing.lg,
  },
  emptyTitle: {
    ...typography.h3,
    color: colors.text,
    marginTop: spacing.md,
    marginBottom: spacing.sm,
  },
  emptySubtitle: {
    ...typography.body2,
    color: colors.textSecondary,
    textAlign: 'center',
  },
  modalContent: {
    backgroundColor: 'white',
    margin: spacing.lg,
    padding: spacing.lg,
    borderRadius: 8,
    maxHeight: '70%',
  },
  modalTitle: {
    ...typography.h3,
    marginBottom: spacing.lg,
    textAlign: 'center',
  },
  filterLabel: {
    ...typography.body1,
    fontWeight: '600',
    marginBottom: spacing.sm,
    marginTop: spacing.md,
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: spacing.lg,
  },
  modalButton: {
    marginLeft: spacing.sm,
  },
});

export default ResultsScreen;
